import { Controller, useFormContext } from "react-hook-form";
import { Mail01, Phone } from "@untitledui/icons";
import { Input, Select } from "@tricura/atlas-ui";
import { FormGroup } from "../../../../components/form-group";
import type { UwInformationFormData } from "../schema";

const durationUnits = [
    { id: "years", label: "Years" },
    { id: "months", label: "Months" },
];

/** Strip anything that isn't a digit, space, parens, dash, or `+` from
 *  a phone-style string so the user can paste a formatted number from
 *  another tool but can't accidentally type letters. Matches the
 *  placeholder shape `+1 (555) 000-0000`. */
const sanitizePhone = (value: string) => value.replace(/[^\d\s+()-]/g, "");

export function GeminiContactsSection() {
    const { control } = useFormContext<UwInformationFormData>();

    return (
        <FormGroup title="Facility Contacts">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Controller
                    control={control}
                    name="geminiContacts.riskManagerName"
                    render={({ field, fieldState }) => (
                        <Input
                            size="sm"
                            label="Risk Manager (RM) Name"
                            placeholder="Enter name"
                            tooltip="Primary risk-management contact at the facility."
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-facility-contacts-risk-manager-name-input"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="geminiContacts.riskManagerEmail"
                    render={({ field, fieldState }) => (
                        <Input
                            size="sm"
                            label="Risk Manager Email"
                            type="email"
                            placeholder="rm@example.com"
                            icon={Mail01}
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-facility-contacts-risk-manager-email-input"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="geminiContacts.riskManagerPhone"
                    render={({ field }) => (
                        <Input
                            size="sm"
                            label="Risk Manager Phone"
                            data-autotest="uw-information-facility-contacts-risk-manager-phone-input"
                            type="tel"
                            inputMode="tel"
                            placeholder="+1 (555) 000-0000"
                            icon={Phone}
                            value={field.value}
                            onChange={(value) => field.onChange(sanitizePhone(value))}
                            onBlur={field.onBlur}
                        />
                    )}
                />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Controller
                    control={control}
                    name="geminiContacts.administratorName"
                    render={({ field, fieldState }) => (
                        <Input
                            size="sm"
                            label="Administrator Name"
                            placeholder="Enter name"
                            data-autotest="uw-information-facility-contacts-administrator-name-input"
                            tooltip="Facility administrator responsible for onboarding."
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="geminiContacts.administratorEmail"
                    render={({ field, fieldState }) => (
                        <Input
                            size="sm"
                            label="Administrator Email"
                            type="email"
                            data-autotest="uw-information-facility-contacts-administrator-email-input"
                            placeholder="admin@example.com"
                            icon={Mail01}
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="geminiContacts.administratorPhone"
                    render={({ field }) => (
                        <Input
                            size="sm"
                            label="Administrator Phone"
                            data-autotest="uw-information-facility-contacts-administrator-phone-input"
                            type="tel"
                            inputMode="tel"
                            placeholder="+1 (555) 000-0000"
                            icon={Phone}
                            value={field.value}
                            onChange={(value) => field.onChange(sanitizePhone(value))}
                            onBlur={field.onBlur}
                        />
                    )}
                />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Controller
                    control={control}
                    name="geminiContacts.administratorDuration"
                    render={({ field, fieldState }) => (
                        <Input
                            size="sm"
                            label="Administrator Duration"
                            data-autotest="uw-information-facility-contacts-administrator-duration-input"
                            type="tel"
                            placeholder="0"
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="geminiContacts.administratorDurationUnit"
                    render={({ field }) => (
                        <Select
                            size="sm"
                            data-autotest="uw-information-facility-contacts-administrator-duration-unit-select"
                            label="Duration Unit"
                            selectedKey={field.value}
                            onSelectionChange={(k) => field.onChange(k as "years" | "months")}
                            items={durationUnits}
                        >
                            {(item) => (
                                <Select.Item id={item.id} data-autotest={`uw-information-facility-contacts-administrator-duration-unit-option-${item.id}`}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    )}
                />
            </div>
        </FormGroup>
    );
}
